// src/components/tiptap/extensions/PracticeTrackerNode.jsx
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plugin, NodeSelection, TextSelection } from 'prosemirror-state';
import { supabase } from '../../../config/supabase';
import { emitToolCompleted } from '../../../lib/mmsToolEvents';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon.jsx';
import StickerBadgeBar from '../../common/stickers/StickerBadgeBar.jsx';
import ConfirmModal from '../../common/ConfirmModal.jsx';


const { FiCheckCircle, FiTrash2, FiPlay, FiPause, FiRotateCcw, FiCheckCircle: FiCheckCircleIcon } = FiIcons;

const CORRECT_URL =
  'https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/correct.mp3';
const CELEBRATE_URL =
  'https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/celebration.mp3';

function uuidv4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
    const h = (n)=>n.toString(16).padStart(2,'0'); const s=Array.from(b,h).join('');
    return `${s.slice(0,8)}-${s.slice(8,12)}-${s.slice(12,16)}-${s.slice(16,20)}-${s.slice(20)}`;
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g,(c)=> {
    const r=(Math.random()*16)|0; const v=c==='x'?r:(r&0x3)|0x8; return v.toString(16);
  });
}

function toast(msg) {
  try {
    let el = document.getElementById('mms-toast');
    if (!el) {
      el = document.createElement('div');
      el.id='mms-toast';
      Object.assign(el.style, {
        position:'fixed', left:'50%', bottom:'28px', transform:'translateX(-50%)',
        padding:'10px 14px', borderRadius:'10px', boxShadow:'0 6px 20px rgba(0,0,0,.18)',
        fontSize:'14px', fontWeight:'600', color:'#201919', background:'#fff',
        border:'1px solid #e5e7eb', opacity:'0', zIndex:10000, pointerEvents:'none',
        transition:'opacity 160ms ease'
      });
      document.body.appendChild(el);
    }
    el.textContent = msg; requestAnimationFrame(()=>{ el.style.opacity='1'; });
    clearTimeout(el._t); el._t=setTimeout(()=>{ el.style.opacity='0'; }, 1600);
  } catch {}
}

function fireGlobalConfetti(opts = {}) {
  try {
    if (typeof document === 'undefined') return;
    const passedCount   = Number.isFinite(opts.count) ? opts.count : null;
    const passedGravity = Number.isFinite(opts.gravity) ? opts.gravity : null;
    const lsCount   = Number.parseInt(localStorage.getItem('mms.confetti.count') || '', 10);
    const lsGravity = Number.parseFloat(localStorage.getItem('mms.confetti.gravity') || '');
    const count   = passedCount   ?? (Number.isFinite(lsCount)   ? lsCount   : 800);
    const gravity = passedGravity ?? (Number.isFinite(lsGravity) ? lsGravity : 0.16);

    const canvas = document.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.inset = '0';
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '9999';
    document.body.appendChild(canvas);

    const DPR = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * DPR);
    canvas.height = Math.round(h * DPR);

    const ctx = canvas.getContext('2d');
    const airDrag = 0.993;
    const maxMs = 8000;
    const bottomBuffer = 32 * DPR;

    const pieces = [];
    for (let i = 0; i < count; i++) {
      pieces.push({
        x: Math.random() * w * DPR,
        y: -Math.random() * (h * DPR * 0.4),
        r: 2 + Math.random() * 4,
        vx: (Math.random() - 0.5) * 3 * DPR,
        vy: (2 + Math.random() * 3) * DPR,
        a: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.25,
        colorIndex: i % 6,
      });
    }
    const colors = ['#f97316', '#2fccba', '#f3958c', '#fffcec', '#6b7280', '#7c3aed'];
    const start = performance.now();
    function draw(now) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let allBelow = true;
      for (let i = 0; i < pieces.length; i++) {
        const p = pieces[i];
        p.vx *= airDrag;
        p.vy = (p.vy + gravity * DPR) * airDrag;
        p.x += p.vx; p.y += p.vy; p.a += p.spin;
        ctx.fillStyle = colors[p.colorIndex];
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.a);
        ctx.fillRect(-p.r, -p.r, p.r * 2, p.r * 2); ctx.restore();
        if (p.y < canvas.height + bottomBuffer) allBelow = false;
      }
      const elapsed = now - start;
      if (!allBelow && elapsed < maxMs) requestAnimationFrame(draw);
      else { ctx.clearRect(0,0,canvas.width,canvas.height); canvas.remove(); }
    }
    requestAnimationFrame(draw);
  } catch (e) { console.warn('confetti error:', e); }
}

// -----------------------------
// Safari-safe retry helpers
// -----------------------------

function isTransientNetworkError(e) {
  const msg = String(e?.message || e || '').toLowerCase();
  return (
    msg.includes('load failed') ||
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network connection was lost') ||
    msg.includes('the network connection was lost')
  );
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function insertPracticeLogWithRetry({ payload, maxAttempts = 4 }) {
  let lastErr = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const ins = await supabase
        .from('practice_logs')
        .insert(payload)
        .select('id, created_at, practiced_date, practiced_seconds, started_at, ended_at')
        .single();

      if (ins.error) throw ins.error;
      return ins.data;
    } catch (e) {
      lastErr = e;

      // Do NOT retry non-network errors (RLS, FK, CHECK, etc.)
      if (!isTransientNetworkError(e)) throw e;

      // User feedback, but keep it lightweight
      if (attempt === 1) {
        toast('Connection dropped — retrying…');
      }

      // Exponential backoff: 300ms, 600ms, 1200ms, 2400ms
      await sleep(300 * Math.pow(2, attempt - 1));
    }
  }

  throw lastErr || new Error('Insert failed');
}

// Tiny debounce helper to prevent RPC storms from realtime + re-renders
function useDebouncedCallback(fn, waitMs = 250) {
  const tRef = useRef(null);
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);
  useEffect(() => {
    return () => {
      try { if (tRef.current) clearTimeout(tRef.current); } catch {}
      tRef.current = null;
    };
  }, []);

  // IMPORTANT: return a stable function identity so effects don't re-run every render.
  return useCallback((...args) => {
    try { if (tRef.current) clearTimeout(tRef.current); } catch {}
    tRef.current = setTimeout(() => {
      try { fnRef.current(...args); } catch {}
    }, waitMs);
  }, [waitMs]);
}

async function deleteTrackerCascade({ trackerId, lessonNoteId, noteBlockId }) {
  let tid = trackerId;
  if (!tid && lessonNoteId && noteBlockId) {
    const r = await supabase
      .from('practice_trackers').select('id')
      .eq('lesson_note_id', lessonNoteId).eq('note_block_id', noteBlockId).maybeSingle();
    tid = r?.data?.id || null;
  }
  if (!tid) return;
  await supabase.from('practice_logs').delete().eq('practice_tracker_id', tid);
  await supabase.from('practice_trackers').delete().eq('id', tid);
}

function PracticeTrackerView({ node, editor, updateAttributes, getPos }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goal, setGoal] = useState(Number(node.attrs.goal) || 5);
  const [goalMinutes, setGoalMinutes] = useState(Math.max(0, Number(node.attrs.goalMinutes ?? 20) || 20));
  const [logCount, setLogCount] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Simple-mode: avoid double counting when we optimistic-increment AND realtime INSERT arrives.
  // We track how many local optimistic inserts are pending acknowledgement via realtime.
  const simpleOptimisticPendingRef = useRef(0);
  
  // Practiceathon (auto-apply on Save; decision persists on node attrs)
  const [practiceathonCandidate, setPracticeathonCandidate] = useState(null);
  const [showPracticeathonModePrompt, setShowPracticeathonModePrompt] = useState(false);

  // IMPORTANT:
  // dbRewarded = "tracker completed in DB" (reward_boolean)
  // celebratedOnce = "we have played celebration in THIS UI session"
  const [dbRewarded, setDbRewarded] = useState(false);
  const [celebratedOnce, setCelebratedOnce] = useState(false);
  const celebratedOnceRef = useRef(false);
  useEffect(() => { celebratedOnceRef.current = celebratedOnce; }, [celebratedOnce]);
  const didInitialHydrateRef = useRef(false);

  // Prevent concurrent duplicate RPCs per-tracker (realtime can spam these)
  const stateRpcInFlightRef = useRef(null);

  // Mode: 'simple' (existing) vs 'advanced' (timer + weekly grid)
  const trackerMode = (node.attrs.trackerMode || 'simple') === 'advanced' ? 'advanced' : 'simple';

  // IMPORTANT: TipTap updateAttributes() is async. If the user changes Mode and immediately clicks Save,
  // node.attrs.trackerMode can still be stale (usually 'simple'), causing the DB row to be created in simple mode.
  // Keep a local draft that updates synchronously and use it for Save.
  const [modeDraft, setModeDraft] = useState(trackerMode);
  const modeDraftRef = useRef(trackerMode);
  useEffect(() => {
    setModeDraft(trackerMode);
    modeDraftRef.current = trackerMode;
  }, [trackerMode]);

  // Track whether the user has started changing settings, so we can avoid “surprise” mode flips.
  // We still prompt early, but this prevents any future auto-adjust logic from fighting them.
  const userTouchedSettingsRef = useRef(false);

  // Debounced refresh (advanced mode needs duration-aware counting)
  const debouncedRefreshState = useDebouncedCallback(async (tid) => {
    try { await fetchTrackerState(tid); } catch {}
  }, 250);

  const currentGoalMinutes = Math.max(0, Number(goalMinutes) || 0);
  const minSessionSeconds = currentGoalMinutes * 60;

  // Advanced: session timer + saved session list
  const [advRunning, setAdvRunning] = useState(false);
  const [advElapsedMs, setAdvElapsedMs] = useState(0); // UI preview only (derived from wall clock)
  const [advStartedAtIso, setAdvStartedAtIso] = useState(null); // wall-clock session start (stable)
  const [advPausedAtIso, setAdvPausedAtIso] = useState(null);   // wall-clock pause start (nullable)
  const [advTotalPausedMs, setAdvTotalPausedMs] = useState(0);   // accumulated paused ms (excludes current pause span)
  const [advSessions, setAdvSessions] = useState([]); // latest saved sessions
  const rafRef = useRef(null);
  // Wall-clock refs (authoritative) to survive sleep/tab suspension reliably
  const advStartedAtMsRef = useRef(null);
  const advPausedAtMsRef = useRef(null);
  const advTotalPausedMsRef = useRef(0);

  const DEFAULT_TZ = 'Australia/Melbourne';
  const MAX_SESSION_MS = 3 * 60 * 60 * 1000; // 3 hours hard cap

  const cls = (...xs) => xs.filter(Boolean).join(' ');

  function pad2(n) { return String(n).padStart(2, '0'); }

  function formatClock(ms) {
    const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`;
  }

  function getLocalIsoDateInTz(d = new Date(), timeZone = DEFAULT_TZ) {
    // Returns YYYY-MM-DD in the provided tz.
    // Using Intl formatToParts keeps this stable across locales.
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);
    const y = parts.find(p => p.type === 'year')?.value;
    const m = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    if (!y || !m || !day) return null;
    return `${y}-${m}-${day}`;
  }

  function formatSessionWhen(iso, timeZone = DEFAULT_TZ) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return new Intl.DateTimeFormat('en-AU', {
        timeZone,
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d);
    } catch {
      return '';
    }
  }

  function clampMs(ms) {
    const n = Number(ms) || 0;
    if (n < 0) return 0;
    return Math.min(n, MAX_SESSION_MS);
  }

  function computeElapsedMs(nowMs = Date.now()) {
    const startedMs = advStartedAtMsRef.current;
    if (!Number.isFinite(startedMs)) return 0;
    const totalPaused = Number(advTotalPausedMsRef.current) || 0;
    const pausedAt = advPausedAtMsRef.current;
    const effectiveEnd = (advRunning && !Number.isFinite(pausedAt)) ? nowMs : (Number.isFinite(pausedAt) ? pausedAt : nowMs);
    return clampMs(effectiveEnd - startedMs - totalPaused);
  }

  async function hydrateAdvancedSessions(tid) {
    const q = await supabase
      .from('practice_logs')
      .select('id, created_at, practiced_date, practiced_seconds, started_at, ended_at')
      .eq('practice_tracker_id', tid)
      .order('created_at', { ascending: false })
      .limit(20);
    if (q.error) return;
    setAdvSessions(Array.isArray(q.data) ? q.data : []);
  }

  function markCelebratedOnce() {
    celebratedOnceRef.current = true;
    setCelebratedOnce(true);
  }

  async function fetchTrackerState(tid, goalFallback = null) {
    // Single round-trip: returns { goal_sessions, goal_minutes, tracker_mode, reward_boolean, log_count }
    // Dedup in-flight requests so realtime spikes don't fan out.
    if (stateRpcInFlightRef.current) return stateRpcInFlightRef.current;
    const p = (async () => {
      const st = await supabase.rpc('get_practice_tracker_state', { p_tracker_id: tid });
      if (st?.error) throw st.error;
      const row = Array.isArray(st?.data) ? st.data[0] : st?.data;
      const dbGoal = Number(row?.goal_sessions ?? (goalFallback ?? 5));
      const dbGoalMinutes = Math.max(
        0,
        Number(row?.goal_minutes ?? (node.attrs.goalMinutes ?? goalMinutes ?? 20)) || 0
      );
      const dbMode = (row?.tracker_mode || node.attrs.trackerMode || 'simple') === 'advanced' ? 'advanced' : 'simple';
      const dbModeVal = (row?.tracker_mode || node.attrs.trackerMode || 'simple') === 'advanced' ? 'advanced' : 'simple';
      const dbCount = Number(row?.log_count ?? 0);
      const dbRewardedNow = !!row?.reward_boolean;

      setGoal(dbGoal);
      setLogCount(dbCount);
      setGoalMinutes(dbGoalMinutes);
      // Track DB completion separately from "celebration played"
      setDbRewarded(dbRewardedNow);

      // If this tracker was already completed BEFORE we opened/refreshed the note,
      // suppress celebration in this UI session.
      // Important: this must work even when node.attrs.trackerId already exists
      // (in that case the earlier "practice_trackers" lookup may not run).
      if (!didInitialHydrateRef.current && dbRewardedNow && !celebratedOnceRef.current) {
        setCelebratedOnce(true);
        celebratedOnceRef.current = true;
      }

      // IMPORTANT: Do NOT call updateAttributes here.
      // Mutating the TipTap doc on every realtime refresh can trigger note autosave loops + statement timeouts.
      return { goal: dbGoal, goalMinutes: dbGoalMinutes, trackerMode: dbModeVal, reward_boolean: dbRewardedNow, count: dbCount };
    })();
    stateRpcInFlightRef.current = p;
    try {
      return await p;
    } finally {
      stateRpcInFlightRef.current = null;
    }
  }

  // -----------------------------
  // Retry wrapper around fetchTrackerState()
  // (keeps React state in sync AND returns normalized {goal, count, reward_boolean})
  // -----------------------------
  async function fetchTrackerStateWithRetry(tid, goalFallback = null, maxAttempts = 4) {
    let lastErr = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fetchTrackerState(tid, goalFallback);
      } catch (e) {
        lastErr = e;
        if (!isTransientNetworkError(e)) throw e;
        if (attempt === 1) toast('Connection dropped — checking progress…');
        await sleep(300 * Math.pow(2, attempt - 1));
      }
    }
    throw lastErr || new Error('State fetch failed');
  }

  // Poll until completion is visible (avoids race where log insert commits before
  // tracker completion update is observable via RPC/read replica).
  async function waitForCompletionSignal({
    tid,
    goalForCheck,
    maxAttempts = 8,
    delayMs = 250,
  }) {
    let last = null;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        last = await fetchTrackerStateWithRetry(tid);
      } catch (e) {
        // If this isn't a transient network error, stop early.
        if (!isTransientNetworkError(e)) throw e;
      }
      const countNow = Number(last?.count ?? 0);
      const rewardedNow = !!last?.reward_boolean;
      const goalNow = Number(last?.goal ?? goalForCheck ?? 0);
      if (rewardedNow || (goalNow > 0 && countNow >= goalNow)) return last;
      await sleep(delayMs);
    }
    return last;
  }
  
  const m = editor?.storage?.mms ?? {};
  const canDelete = !!m?.userId && !!editor?.isEditable; // teachers only
  
  const focusedOnceRef = useRef(false);

  // -----------------------------
  // Practiceathon lookup (notes context)
  // -----------------------------

  function getPracticeathonDecisionFromAttrs() {
    return {
      apply: node?.attrs?.applyToPracticeathon,
      practiceathonId: node?.attrs?.practiceathonId,
      practiceathonEnrolmentId: node?.attrs?.practiceathonEnrolmentId,
      practiceathonName: node?.attrs?.practiceathonName,
    };
  }

  async function fetchPracticeathonCandidateForThisNoteContext() {
    try {
      const m = editor?.storage?.mms ?? {};
      const p_school_id = m?.schoolId ?? m?.school_id ?? null;
      const p_student_master_id = m?.masterStudentId ?? m?.studentMasterId ?? m?.student_master_id ?? null;

      // Notes context is deterministic:
      // - Individual note: node.attrs.studentId is students.id (enrolment)
      // - Group note: school app knows group_id in editor.storage.mms (if available)
      const p_student_id = node?.attrs?.studentId ?? null;
      const p_group_id = m?.groupId ?? m?.group_id ?? null;

      if (!p_school_id || !p_student_master_id) return null;

      const res = await supabase.rpc('get_active_practiceathons_for_student_context', {
        p_school_id,
        p_student_master_id,
        p_student_id,
        p_group_id,
      });
      if (res?.error) throw res.error;
      const rows = Array.isArray(res?.data) ? res.data : [];
      if (!rows.length) return null;

      // If function returns more than one row, prefer the default.
      const best = rows.find(r => !!r.is_default) || rows[0];
      if (!best?.practiceathon_id || !best?.practiceathon_enrolment_id) return null;

      return {
        practiceathon_id: best.practiceathon_id,
        practiceathon_name: best.practiceathon_name,
        practiceathon_mode: best.practiceathon_mode,
        practiceathon_enrolment_id: best.practiceathon_enrolment_id,
      };
    } catch {
      // Best-effort only; do not break tracker if lookup fails/RLS blocks.
      return null;
    }
  }

  function getPracticeathonFieldsForLogInsert() {
    const d = getPracticeathonDecisionFromAttrs();
    if (!d.apply) return {};
    if (!d.practiceathonId || !d.practiceathonEnrolmentId) return {};
    return {
      practiceathon_id: d.practiceathonId,
      practiceathon_enrolment_id: d.practiceathonEnrolmentId,
    };
  }

  function recommendedTrackerModeForPracticeathon(practiceathonMode) {
    // practiceathon_mode enum is public.practiceathon_mode; you’ve shown values like "time".
    // Anything time-based should map to advanced (timer). Everything else defaults to simple.
    if (practiceathonMode === 'time') return 'advanced';
    return 'simple';
  }

  // Ensure essential attrs
  useEffect(() => {
    const patch = {};
    const m = editor?.storage?.mms ?? {};
    if (!node.attrs.noteBlockId) patch.noteBlockId = uuidv4();
    if (!node.attrs.lessonNoteId && m?.lessonNoteId) patch.lessonNoteId = m.lessonNoteId;
    if (!node.attrs.studentId && m?.studentId) patch.studentId = m.studentId;
    if (!node.attrs.trackerMode) patch.trackerMode = 'simple';
    if (node.attrs.goalMinutes == null) patch.goalMinutes = 20;
    if (node.attrs.practiceathonModePrompted == null) patch.practiceathonModePrompted = false;

    // Do NOT prompt on existing trackers opened later; only prompt on Save.
    // If this is an older tracker doc with no decision saved, default to "don't count".
    if (node.attrs.trackerId && node.attrs.applyToPracticeathon == null) {
      patch.applyToPracticeathon = false;
    }

    if (Object.keys(patch).length) updateAttributes(patch);
  }, [editor, node.attrs.noteBlockId, node.attrs.lessonNoteId, node.attrs.studentId, updateAttributes]);

  // ✅ Early Practiceathon-mode prompt (before Save, before fiddling)
  // Only for NEW/unsaved trackers, and only once per node.
  useEffect(() => {
    let cancelled = false;
    async function maybePromptMode() {
      try {
        if (cancelled) return;
        if (node?.attrs?.trackerId) return; // already saved; don’t change UX retroactively
        if (node?.attrs?.practiceathonModePrompted) return; // already prompted for this node
        // Only prompt if we have enough context to find a practiceathon deterministically
        const m = editor?.storage?.mms ?? {};
        if (!m?.schoolId && !m?.school_id) return;
        if (!m?.masterStudentId && !m?.studentMasterId && !m?.student_master_id) return;

        const cand = await fetchPracticeathonCandidateForThisNoteContext();
        if (cancelled) return;
        if (!cand?.practiceathon_id || !cand?.practiceathon_mode) return;

        const recommended = recommendedTrackerModeForPracticeathon(cand.practiceathon_mode);
        const current = (node.attrs.trackerMode || 'simple') === 'advanced' ? 'advanced' : 'simple';

        // If already aligned, don’t bother.
        if (recommended === current) {
          updateAttributes({ practiceathonModePrompted: true });
          return;
        }

        // Prompt early so they don’t waste time setting up the wrong mode.
        setPracticeathonCandidate(cand);
        setShowPracticeathonModePrompt(true);
      } catch {
        // Best-effort only
      }
    }
    maybePromptMode();
    return () => { cancelled = true; };
    // Intentionally not depending on practiceathonCandidate to avoid loops.
  }, [editor, node?.attrs?.trackerId, node?.attrs?.practiceathonModePrompted]);

  // hydrate goal + count
  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      const lid = node.attrs.lessonNoteId;
      const nbid = node.attrs.noteBlockId;
      if (!lid || !nbid) return;
      try {
        let tid = node.attrs.trackerId;
        let goalFromDb = null;
        let modeFromDb = node.attrs.trackerMode || 'simple';

        if (!tid) {
          const ref = await supabase
            .from('practice_trackers')
            .select('id, goal_sessions, goal_minutes, tracker_mode, reward_boolean')
            .eq('lesson_note_id', lid)
            .eq('note_block_id', nbid)
            .maybeSingle();
          if (ref?.data?.id) {
            tid = ref.data.id;
            goalFromDb = ref.data.goal_sessions ?? null;
            modeFromDb = ref.data.tracker_mode || 'simple';
            
            if (!cancelled) setDbRewarded(!!ref.data.reward_boolean);
            // If this tracker was already completed before we opened the note,
            // treat it as already celebrated so we don't fire celebration on load.
            if (!cancelled && !didInitialHydrateRef.current && !!ref.data.reward_boolean) {
              setCelebratedOnce(true);
              celebratedOnceRef.current = true;
            }

            updateAttributes({
              trackerId: tid,
              goal: ref.data.goal_sessions,
              goalMinutes: ref.data.goal_minutes ?? (node.attrs.goalMinutes ?? 20),
              trackerMode: modeFromDb,
            });
            if (!cancelled) setGoal(ref.data.goal_sessions || 5);
            if (!cancelled) setGoalMinutes(Math.max(0, Number(ref.data.goal_minutes ?? (node.attrs.goalMinutes ?? 20)) || 0));
          }
        }
        if (!tid) return;
        if (cancelled) return;
        // Single call for goal + count
        // rewarded flag now comes from fetchTrackerState() RPC
        await fetchTrackerState(tid, goalFromDb);
        // Advanced: hydrate latest session list
        if (!cancelled && modeFromDb === 'advanced') await hydrateAdvancedSessions(tid);
        if (!cancelled) didInitialHydrateRef.current = true;
      } catch {}
    }
    hydrate();
    return () => { cancelled = true; };
  }, [node.attrs.lessonNoteId, node.attrs.noteBlockId, node.attrs.trackerId, updateAttributes]);
 
  // realtime for logs
  useEffect(() => {
    const tid = node.attrs.trackerId;
    if (!tid) return;
    const ch = supabase.channel(`pt-${tid}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'practice_logs', filter: `practice_tracker_id=eq.${tid}` },
        () => {
          const modeNow = (node.attrs.trackerMode || 'simple') === 'advanced' ? 'advanced' : 'simple';
          if (modeNow === 'advanced') {
            // Debounce duration-aware refresh to avoid request storms
            debouncedRefreshState(tid);
            hydrateAdvancedSessions(tid);
          } else {
            // Simple mode: realtime ack for local optimistic insert OR a remote insert.
            if (simpleOptimisticPendingRef.current > 0) {
              simpleOptimisticPendingRef.current -= 1;
              return;
            }
            setLogCount(c => c + 1);
          }
        })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'practice_logs', filter: `practice_tracker_id=eq.${tid}` },
        () => {
          const modeNow = (node.attrs.trackerMode || 'simple') === 'advanced' ? 'advanced' : 'simple';
          if (modeNow === 'advanced') {
            debouncedRefreshState(tid);
            hydrateAdvancedSessions(tid);
          } else {
            setLogCount(c => Math.max(0, c - 1));
          }
        })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [node.attrs.trackerId, debouncedRefreshState]);

  const isSaved = !!node.attrs.trackerId;
  const currentGoal = Math.max(1, Number(goal) || 1);
  const filled = Math.min(logCount, currentGoal);
  const isComplete = filled >= currentGoal;
  const nextIndex = filled;
  const canClickStars = isSaved && !saving && !isComplete;

  async function handleSave() {
    if (saving) return;
    setSaving(true); setErr(null);

    // IMPORTANT:
    // TipTap node attrs may not be hydrated yet on first mount (effects haven't run),
    // so we must derive required values from editor.storage.mms and/or generate them,
    // and then persist them to attrs. Do NOT block Save on "stale" node.attrs.
    const m = editor?.storage?.mms ?? {};
    const lid = node.attrs.lessonNoteId || m?.lessonNoteId || null;
    const sid = node.attrs.studentId || m?.studentId || null;
    const nbid = node.attrs.noteBlockId || uuidv4();

    const seedPatch = {};
    if (!node.attrs.noteBlockId) seedPatch.noteBlockId = nbid;
    if (!node.attrs.lessonNoteId && lid) seedPatch.lessonNoteId = lid;
    if (!node.attrs.studentId && sid) seedPatch.studentId = sid;
    if (!node.attrs.trackerMode) seedPatch.trackerMode = 'simple';
    if (node.attrs.goalMinutes == null) seedPatch.goalMinutes = 20;
    if (node.attrs.practiceathonModePrompted == null) seedPatch.practiceathonModePrompted = false;
    if (Object.keys(seedPatch).length) updateAttributes(seedPatch);

    if (!lid || !sid || !nbid) { setSaving(false); setErr('Missing required attributes.'); return; }
    try {
      // IMPORTANT: lock intended mode/minutes BEFORE we touch DB, to avoid "flash" (advanced -> simple -> advanced)
      const intendedMode =
        (modeDraftRef.current || 'simple') === 'advanced' ? 'advanced' : 'simple';
      const intendedMinutes = Math.max(0, Number(goalMinutes) || 0);

      const rpc = await supabase.rpc('get_or_create_practice_tracker', {
        p_lesson_note_id: lid, p_note_block_id: nbid, p_student_id: sid, p_goal: Math.max(1, Number(goal) || 5),
      });
      if (rpc.error) throw rpc.error;
      const row = Array.isArray(rpc.data) ? rpc.data[0] : rpc.data;
      if (!row?.id) throw new Error('Tracker RPC returned no row');
      // Keep UI stable immediately after save.
      updateAttributes({
        trackerId: row.id,
        goal: row.goal_sessions,
        trackerMode: intendedMode,
        goalMinutes: intendedMinutes,
      });
      setGoal(row.goal_sessions || 5);

      // Persist mode + minutes FIRST, then hydrate state.
      // This prevents a render cycle where get_practice_tracker_state returns tracker_mode='simple'
      // (default) before we update it, causing the "flash".
      await supabase.from('practice_trackers')
        .update({
          tracker_mode: intendedMode,
          goal_minutes: intendedMode === 'advanced' ? intendedMinutes : null,
        })
        .eq('id', row.id);

      await fetchTrackerState(row.id, row.goal_sessions ?? null);
      if (intendedMode === 'advanced') {
        await hydrateAdvancedSessions(row.id);
      }

      // ✅ Practiceathon auto-apply (ONLY ONCE, after first Save)
      // Make Save feel snappy: do NOT block on the Practiceathon lookup.
      if (node.attrs.applyToPracticeathon == null) {
        (async () => {
          const cand = await fetchPracticeathonCandidateForThisNoteContext();
          if (cand?.practiceathon_id && cand?.practiceathon_enrolment_id) {
            // Persist decision so we never re-check for this node.
            updateAttributes({
              applyToPracticeathon: true,
              practiceathonId: cand.practiceathon_id,
              practiceathonEnrolmentId: cand.practiceathon_enrolment_id,
              practiceathonName: cand.practiceathon_name,
            });

            // If the practiceathon is time-based, ensure the tracker is Advanced (timer).
            // If the practiceathon is session-based, ensure the tracker is Simple (stars).
            // We do this immediately after the auto-apply so they don't waste time configuring the wrong mode.
            try {
              const mode = cand.practiceathon_mode || null; // 'time' | 'sessions'
              const desiredTrackerMode = (mode === 'time') ? 'advanced' : (mode === 'sessions' ? 'simple' : null);

              if (desiredTrackerMode && desiredTrackerMode !== (node?.attrs?.trackerMode || 'simple')) {
                // Switch UI mode
                updateAttributes({ trackerMode: desiredTrackerMode });

                // If switching away from advanced, clear any in-progress session UI state.
                if (desiredTrackerMode !== 'advanced') {
                  try { advancedReset(); } catch {}
                }

                // Keep DB tracker_mode aligned too (tracker is already saved at this point).
                const tid = row.id;
                if (tid) {
                  await supabase
                    .from('practice_trackers')
                    .update({
                      tracker_mode: desiredTrackerMode,
                      goal_minutes: desiredTrackerMode === 'advanced'
                        ? Math.max(1, Number(goalMinutes) || 20)
                        : null,
                    })
                    .eq('id', tid);
                }

                toast(
                  desiredTrackerMode === 'advanced'
                    ? 'Practiceathon is time-based — switched to Timer mode ⏱️'
                    : 'Practiceathon is session-based — switched to Stars mode ⭐'
                );
              }
            } catch (e) {
              console.warn('Mode alignment failed during auto-apply:', e);
            }

            setPracticeathonCandidate(cand);
            // Optional: one lightweight toast; remove if you prefer silence.
            toast('Counting towards Practiceathon ✅');
          } else {
            // Persist "no" so we never ask again for this tracker node.
            updateAttributes({
              applyToPracticeathon: false,
              practiceathonId: null,
              practiceathonEnrolmentId: null,
              practiceathonName: null,
            });
          }
        })();
      }
    } catch (e) { setErr(e?.message || 'Failed to save'); } finally { setSaving(false); }
  }

  async function ensureTrackerId() {
    // If we already have a trackerId, trust it (avoid an extra network round-trip)
    if (node.attrs.trackerId) {
      return node.attrs.trackerId;
    }
    const ref = await supabase.from('practice_trackers').select('id, goal_sessions, reward_boolean')
      .eq('lesson_note_id', node.attrs.lessonNoteId).eq('note_block_id', node.attrs.noteBlockId).maybeSingle();
    if (ref.data?.id) {
      updateAttributes({ trackerId: ref.data.id, goal: ref.data.goal_sessions });
      setGoal(ref.data.goal_sessions || goal);
      setDbRewarded(!!ref.data.reward_boolean);
      return ref.data.id;
    }
    const rpc = await supabase.rpc('get_or_create_practice_tracker', {
      p_lesson_note_id: node.attrs.lessonNoteId,
      p_note_block_id: node.attrs.noteBlockId,
      p_student_id: node.attrs.studentId,
      p_goal: Math.max(1, Number(goal) || 5),
    });
    if (rpc.error) throw rpc.error;
    const row = Array.isArray(rpc.data) ? rpc.data[0] : rpc.data;
    updateAttributes({ trackerId: row.id, goal: row.goal_sessions });
    setGoal(row.goal_sessions || goal);
    // Keep DB mode in sync (idempotent)
    const mode = (node.attrs.trackerMode || 'simple') === 'advanced' ? 'advanced' : 'simple';
    await supabase.from('practice_trackers')
      .update({ tracker_mode: mode, goal_minutes: mode === 'advanced' ? Math.max(0, Number(goalMinutes) || 0) : null })
      .eq('id', row.id);
    updateAttributes({ trackerMode: mode });
    return row.id;
  }

  async function handleStarClick(index) {
    if (!canClickStars || index !== nextIndex) return;
    setSaving(true);
    try {
      let tid = await ensureTrackerId();
      const sid = node.attrs.studentId;
      const practiceathonFields = getPracticeathonFieldsForLogInsert();
      // Faster: don't round-trip the inserted row (we don't use it here).
      const tryInsert = async () =>
        supabase
          .from('practice_logs')
          .insert(
            { practice_tracker_id: tid, student_id: sid, ...practiceathonFields },
            { returning: 'minimal' }
          );
      let ins = await tryInsert();
      if (ins.error?.code === '23503') { tid = await ensureTrackerId(); ins = await tryInsert(); }
      if (ins.error) throw ins.error;
      // Optimistic update: faster UI.
      // Realtime will also emit an INSERT shortly; we suppress that one increment via simpleOptimisticPendingRef.
      simpleOptimisticPendingRef.current += 1;
      const newCount = filled + 1;
      setLogCount(newCount);
      try { await new Audio(CORRECT_URL).play(); } catch {}
      // Celebrate + sticker only once per tracker
      if (newCount >= currentGoal && !celebratedOnceRef.current) {
        try { await new Audio(CELEBRATE_URL).play(); } catch {}
        fireGlobalConfetti({ count: 1200 });
        emitToolCompleted({ noteBlockId: node?.attrs?.noteBlockId, earnedFor: 'Completed practice goal' });
        markCelebratedOnce();
      }
    } catch (e) { console.warn('log failed:', e); } finally { setSaving(false); }
  }

  async function handleUpdateGoal(newGoal) {
    if (!isSaved || saving) return;
    const safe = Math.max(1, Math.min(100, Number(newGoal) || 1));
    const prev = goal; setGoal(safe);
    try {
      const tid = node.attrs.trackerId;
      const upd = await supabase.from('practice_trackers').update({ goal_sessions: safe }).eq('id', tid).select('id, goal_sessions');
      if (upd.error) throw upd.error;
      const dbGoal = Array.isArray(upd.data) && upd.data.length ? upd.data[0].goal_sessions : safe;
      updateAttributes({ goal: dbGoal });
      await fetchTrackerState(tid, dbGoal);
    } catch { setGoal(prev); }
  }

  async function handleDelete() {
    setShowDeleteConfirm(true);
  }

  const stars = useMemo(() => Array.from({ length: Math.max(1, Number(goal) || 1) }), [goal]);

  // Advanced timer (RAF-based, like TimerTool) — fixes "timer doesn't start" + keeps it smooth
  useEffect(() => {
    return () => {
      try {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      } catch {}
      rafRef.current = null;
    };
  }, []);

  const stopLoop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const tick = () => {
    // UI preview is wall-clock derived; RAF is only for smooth updates.
    setAdvElapsedMs(computeElapsedMs(Date.now()));
    rafRef.current = requestAnimationFrame(tick);
  };

  const advancedStart = () => {
    if (!isSaved || saving) return;
    if (advRunning) return;
    const nowMs = Date.now();

    // Fresh session: capture stable wall-clock started_at
    if (!advStartedAtIso || !Number.isFinite(advStartedAtMsRef.current)) {
      const iso = new Date(nowMs).toISOString();
      setAdvStartedAtIso(iso);
      advStartedAtMsRef.current = nowMs;
      advTotalPausedMsRef.current = 0;
      setAdvTotalPausedMs(0);
      advPausedAtMsRef.current = null;
      setAdvPausedAtIso(null);
    }

    // Resume from paused: accrue pause time up to now, then clear paused marker
    if (Number.isFinite(advPausedAtMsRef.current)) {
      const pausedDelta = Math.max(0, nowMs - advPausedAtMsRef.current);
      const nextTotal = (Number(advTotalPausedMsRef.current) || 0) + pausedDelta;
      advTotalPausedMsRef.current = nextTotal;
      setAdvTotalPausedMs(nextTotal);
      advPausedAtMsRef.current = null;
      setAdvPausedAtIso(null);
    }

    setAdvRunning(true);
    setAdvElapsedMs(computeElapsedMs(nowMs));
    rafRef.current = requestAnimationFrame(tick);
  };

  const advancedPause = () => {
    if (!advRunning) return;
    stopLoop();
    const nowMs = Date.now();
    advPausedAtMsRef.current = nowMs;
    setAdvPausedAtIso(new Date(nowMs).toISOString());
    setAdvRunning(false);
    setAdvElapsedMs(computeElapsedMs(nowMs));
  };

  const advancedReset = () => {
    stopLoop();
    setAdvRunning(false);
    advStartedAtMsRef.current = null;
    advPausedAtMsRef.current = null;
    advTotalPausedMsRef.current = 0;
    setAdvElapsedMs(0);
    setAdvStartedAtIso(null);
    setAdvPausedAtIso(null);
    setAdvTotalPausedMs(0);
  };

  async function advancedFinish() {
    if (!isSaved || saving) return;
    // Stop RAF; we will save by wall clock (started_at -> ended_at, minus paused intervals).
    stopLoop();

    const endNowMs = Date.now();

    // If currently running, stop it logically (do not create a paused interval)
    if (advRunning) {
      setAdvRunning(false);
    }

    // If currently paused, extend the paused interval up to end time (so paused tail is excluded).
    if (Number.isFinite(advPausedAtMsRef.current)) {
      const pausedDelta = Math.max(0, endNowMs - advPausedAtMsRef.current);
      const nextTotal = (Number(advTotalPausedMsRef.current) || 0) + pausedDelta;
      advTotalPausedMsRef.current = nextTotal;
      setAdvTotalPausedMs(nextTotal);
      advPausedAtMsRef.current = null;
      setAdvPausedAtIso(null);
    }

    let effectiveMs = computeElapsedMs(endNowMs);
    if (effectiveMs >= MAX_SESSION_MS) {
      // Hard cap for runaway sessions (e.g. left overnight)
      effectiveMs = MAX_SESSION_MS;
      toast('Session capped at 3 hours.');
    }

    const seconds = Math.max(0, Math.round(effectiveMs / 1000));
    if (seconds <= 0) { toast('Nothing to save yet.'); return; }

    setSaving(true);
    try {
      const tid = await ensureTrackerId();
      const sid = node.attrs.studentId;
      const tz = editor?.storage?.mms?.timezone || DEFAULT_TZ;
      const practiceathonFields = getPracticeathonFieldsForLogInsert();

      // Persist by wall clock, not by UI preview timer.
      const startedAtIso = advStartedAtIso || new Date(endNowMs).toISOString();
      const endedAtIso = new Date(endNowMs).toISOString();
      const practicedDate = getLocalIsoDateInTz(new Date(endedAtIso), tz);
      if (!practicedDate) throw new Error('Could not compute practiced date');

      const row = await insertPracticeLogWithRetry({
        payload: {
          practice_tracker_id: tid,
          student_id: sid,
          practiced_seconds: seconds,
          practiced_date: practicedDate,
          started_at: startedAtIso,
          ended_at: endedAtIso,
          ...practiceathonFields,
        },
      });

      // For advanced: do NOT blindly increment progress (short sessions don't count).
      // Refresh from DB (duration-aware). This MUST be resilient to transient network drops
      // or completion celebration can be missed (advanced does not optimistic-increment).
      let stNow = null;
      try {
        // First fetch, then (if needed) poll briefly for the completion flip.
        const first = await fetchTrackerStateWithRetry(tid);
        const goalForCheck = Number(first?.goal ?? currentGoal);
        const countNow = Number(first?.count ?? 0);
        const rewardedNow = !!first?.reward_boolean;
        if (rewardedNow || (goalForCheck > 0 && countNow >= goalForCheck)) {
          stNow = first;
        } else {
          stNow = await waitForCompletionSignal({ tid, goalForCheck });
        }
      } catch {
        stNow = null;
      }

      setAdvSessions(prev => {
        const arr = Array.isArray(prev) ? prev : [];
        // Realtime/hydrate may have already pulled this row in; avoid UI duplicates.
        if (arr.some(r => r?.id === row?.id)) return arr;
        return [row, ...arr].slice(0, 20);
      });

      // Audio on every saved session
      try { await new Audio(CORRECT_URL).play(); } catch {}

      // Celebration + sticker only once per tracker, when goal is met (duration-aware)
      const effectiveCount = Number(stNow?.count ?? (Number(logCount) || 0));
      const dbRewardedNow = !!stNow?.reward_boolean;
      const goalForCheck = Number(stNow?.goal ?? currentGoal);

      // In parent/student contexts, direct table reads may be blocked by RLS.
      // The RPC is the authoritative signal; celebrate if either:
      //  - duration-aware count meets goal, OR
      //  - reward_boolean has flipped true (trigger marked completion)
      const shouldCelebrate = (effectiveCount >= goalForCheck) || dbRewardedNow;

      if (shouldCelebrate && !celebratedOnceRef.current) {
        try { await new Audio(CELEBRATE_URL).play(); } catch {}
        fireGlobalConfetti({ count: 1200 });
        emitToolCompleted({ noteBlockId: node?.attrs?.noteBlockId, earnedFor: 'Completed practice goal' });
        markCelebratedOnce();
      }

      // Reset for next session
      advancedReset();
    } catch (e) {
      console.warn('advanced finish failed:', e);
      toast('Sorry—could not save time.');
    } finally {
      setSaving(false);
    }
  }

  if (err) {
    return (
      <NodeViewWrapper className="rounded-2xl border p-4 bg-[#fffcec] border-[#f97316]/30">
        <div className="text-sm text-[#201919]">{String(err)}</div>
        <button className="mt-2 px-2 py-1 rounded bg-white border border-black/10 text-[#201919] hover:bg-black/5" onClick={() => setErr(null)}>Dismiss</button>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      className="relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200"
      data-note-block-id={node.attrs.noteBlockId}
      style={{ position: 'relative' }}
    >
      {showPracticeathonModePrompt && practiceathonCandidate && (
        <ConfirmModal
          title="Practiceathon is active"
          message={
            (() => {
              const pm = practiceathonCandidate.practiceathon_mode;
              const rec = recommendedTrackerModeForPracticeathon(pm);
              const niceMode =
                rec === 'advanced'
                  ? 'Timer mode (time-based sessions)'
                  : 'Session mode (tick sessions)';
              return `“${practiceathonCandidate.practiceathon_name}” is currently running and is ${pm}-based. Switch this tracker to ${niceMode} now so you don’t have to redo your setup?`;
            })()
          }
          confirmText="Yes, switch"
          cancelText="Keep as-is"
          confirmButtonClass="bg-orange-500 hover:bg-orange-600"
          confirmDisabled={saving}
          onCancel={() => {
            userTouchedSettingsRef.current = true;
            updateAttributes({ practiceathonModePrompted: true });
            setShowPracticeathonModePrompt(false);
          }}
          onClose={() => {
            userTouchedSettingsRef.current = true;
            updateAttributes({ practiceathonModePrompted: true });
            setShowPracticeathonModePrompt(false);
          }}
          onConfirm={async () => {
            userTouchedSettingsRef.current = true;
            const pm = practiceathonCandidate.practiceathon_mode;
            const recommended = recommendedTrackerModeForPracticeathon(pm);

            // Switch UI mode immediately (before Save). This does NOT opt them into counting;
            // that separate prompt still happens on Save as before.
            const patch = { trackerMode: recommended, practiceathonModePrompted: true };

            // If switching away from advanced, reset any draft timer state.
            if (recommended !== 'advanced') {
              try { advancedReset(); } catch {}
            }

            // If switching to advanced and goalMinutes is missing/0, keep your existing default.
            if (recommended === 'advanced') {
              const v = Math.max(1, Number(node.attrs.goalMinutes ?? goalMinutes ?? 20) || 20);
              patch.goalMinutes = v;
            }

            updateAttributes(patch);
            setShowPracticeathonModePrompt(false);
            toast('Tracker mode updated ✅');
          }}
        />
      )}

      {/* Practiceathon counting is now automatic on first Save (no prompt). */}

      {showDeleteConfirm && (
        <ConfirmModal
          title="Delete practice tracker?"
          message="Delete this practice tracker (and its logs)? This cannot be undone."
          confirmText={saving ? 'Deleting…' : 'Delete'}
          cancelText="Cancel"
          confirmButtonClass="bg-red-500 hover:bg-red-600"
          confirmDisabled={saving}
          onCancel={() => setShowDeleteConfirm(false)}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            setSaving(true);
            try {
              await deleteTrackerCascade({
                trackerId: node.attrs.trackerId,
                lessonNoteId: node.attrs.lessonNoteId,
                noteBlockId: node.attrs.noteBlockId,
              });
              const pos = typeof getPos === 'function' ? getPos() : null;
              if (typeof pos === 'number') {
                editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).insertContent({ type: 'paragraph' }).run();
              } else {
                updateAttributes({ trackerId: null });
                setLogCount(0);
              }
            } catch {
              toast('Sorry—could not delete this tracker.');
            } finally {
              setSaving(false);
              setShowDeleteConfirm(false);
            }
          }}
        />
      )}

  {/* Sticker badges: top border/right, half outside */}
  <div className="absolute right-3 top-0 translate-y-[-50%] z-10">
    <StickerBadgeBar noteBlockId={node.attrs.noteBlockId} max={10} />
  </div>

      {/* Header — match Memory Match */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiCheckCircle} className="w-6 h-6" />
          </div>
          <div>
            <div className="text-lg font-bold text-slate-800">Practice Trackers</div>
            <div className="text-sm text-slate-500">Meet your practice goals this week.</div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            className="text-xs px-2 py-1 rounded-lg border bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400"
            onClick={handleSave}
            disabled={saving || isSaved}
            title={isSaved ? 'Already saved' : 'Save'}
          >
            {isSaved ? 'Saved' : (saving ? 'Saving…' : 'Save')}
          </button>
          {canDelete && (
            <button
                className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
                onClick={handleDelete}
                disabled={saving}
                title="Delete Tool"
                aria-label="Delete Tool"
            >
                <SafeIcon icon={FiTrash2} className="w-5 h-5" />
            </button>
            )}
        </div>
      </div>

      {/* Controls */}
      <div className="mb-4 flex items-center gap-3">
        {/* Mode selector (only before save) */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Mode:</span>
          <select
            className="h-9 px-2 rounded-lg border border-slate-300 bg-white text-slate-700"
            value={modeDraft}
            disabled={isSaved || saving}
            onChange={(e) => {
              userTouchedSettingsRef.current = true;
              const v = e.target.value === 'advanced' ? 'advanced' : 'simple';
              setModeDraft(v);
              modeDraftRef.current = v;
              updateAttributes({ trackerMode: v });
              // Reset any running advanced session if switching away
              if (v !== 'advanced') {
                advancedReset();
              }
            }}
            title={isSaved ? 'Mode is locked after saving' : 'Choose Simple or Advanced'}
          >
            <option value="simple">Simple</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        <span className="text-sm text-slate-600">Goal sessions:</span>

        {!editingGoal ? (
          <div className="flex items-center gap-2">
            <button
              className="h-9 w-9 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 text-xl leading-none"
              onClick={() => (isSaved ? handleUpdateGoal(Math.max(1, currentGoal - 1)) : setGoal(Math.max(1, currentGoal - 1)))}
              disabled={saving}
              title="Decrease goal"
            >
              −
            </button>
            <button
              className="px-4 h-9 rounded-lg bg-white border border-orange-300 text-orange-600 font-bold text-lg shadow-sm"
              onClick={() => setEditingGoal(true)}
              title="Click to edit goal"
            >
              {currentGoal}
            </button>
            <button
              className="h-9 w-9 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 text-xl leading-none"
              onClick={() => (isSaved ? handleUpdateGoal(currentGoal + 1) : setGoal(currentGoal + 1))}
              disabled={saving}
              title="Increase goal"
            >
              +
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="number" min="1" max="100"
              className="w-24 h-9 px-2 rounded-lg border border-orange-300 focus:outline-none text-lg font-semibold text-orange-600 text-center bg-white"
              value={currentGoal}
              onChange={(e) => setGoal(Number(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); setEditingGoal(false); if (isSaved) handleUpdateGoal(currentGoal); }
                if (e.key === 'Escape') { e.preventDefault(); setEditingGoal(false); }
              }}
              autoFocus
            />
            <button
              className="h-9 px-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => { setEditingGoal(false); if (isSaved) handleUpdateGoal(currentGoal); }}
            >
              Save
            </button>
            <button
              className="h-9 px-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-100"
              onClick={() => { setEditingGoal(false); setGoal(node.attrs.goal || 5); }}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Advanced: inline duration control (to the right of goal sessions) */}
        {trackerMode === 'advanced' && (
          <div className="flex items-center gap-2 ml-2">
            <span className="text-sm font-semibold text-slate-700">Duration</span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="180"
                className="w-20 h-9 px-2 rounded-lg border border-orange-300 bg-white text-orange-700 text-center font-extrabold shadow-sm"
                value={Math.max(1, currentGoalMinutes || 20)}
                disabled={saving}
                onChange={(e) => {
                  userTouchedSettingsRef.current = true;
                  const v = Math.max(1, Math.min(180, Number(e.target.value) || 20));
                  setGoalMinutes(v);
                  updateAttributes({ goalMinutes: v });
                }}
                onBlur={async () => {
                  if (!node.attrs.trackerId) return;
                  try {
                    const v = Math.max(1, Number(goalMinutes) || 20);
                    // Avoid redundant DB writes (can add up on busy notes)
                    if (Number(node.attrs.goalMinutes) === v) return;
                    await supabase.from('practice_trackers').update({ goal_minutes: v }).eq('id', node.attrs.trackerId);
                    await fetchTrackerState(node.attrs.trackerId);
                  } catch {}
                }}
                title="In Advanced mode, a session only counts toward your goal if it meets or exceeds this duration."
              />
              <span className="text-sm text-slate-600">min</span>
            </div>
          </div>
        )}

        <span className="ml-auto text-sm text-slate-500">{filled}/{currentGoal} logged</span>
      </div>

      {/* Stars */}
      {isSaved && trackerMode === 'simple' && (
        <div className="flex flex-wrap items-center justify-center gap-2 mb-1">
          {stars.map((_, i) => {
            const isFilled = i < filled;
            const isClickable = canClickStars && i === nextIndex;
            return (
              <button
                type="button"
                key={i}
                onClick={() => isClickable && handleStarClick(i)}
                className={`transition transform ${isFilled ? 'text-orange-500' : 'text-slate-300'}
                  ${isClickable ? 'hover:scale-110 active:scale-95 cursor-pointer' : 'opacity-90'} text-[28px] sm:text-[32px]`}
                aria-disabled={!isClickable}
                style={{ lineHeight: 1, cursor: isClickable ? 'pointer' : 'default' }}
                title={isFilled ? 'Logged' : (isClickable ? 'Click to log practice' : (filled >= currentGoal ? 'Goal complete' : 'Complete previous step first'))}
              >
                <span className="inline-block">{isFilled ? '★' : '☆'}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Advanced session timer + saved sessions list */}
      {isSaved && trackerMode === 'advanced' && (
        <div className="mt-2">
          {(() => {
            const draftMs = Number(advElapsedMs) || 0;
            const hasDraft = advRunning || draftMs > 0 || !!advStartedAtIso;
            const canFinish = !saving && draftMs > 0;
            const showReset = !saving && !advRunning && draftMs > 0;
            // Keep cards in the order they were generated (oldest -> newest), with the
            // "next session" placeholder always to the right of the most recent.
            const sessionsForDisplay = (Array.isArray(advSessions) ? advSessions : []).slice().reverse();
            const saveCompact = showReset; // when paused (and reset is visible), keep save icon-only to avoid squashing
            return (
              <div className="flex flex-wrap gap-3">
                {/* Saved sessions: oldest first (left-to-right) */}
                {sessionsForDisplay.map((s, idx) => {
                  const secs = Number(s?.practiced_seconds) || 0;
                  const ms = secs * 1000;
                  return (
                    <div
                      key={s.id}
                      className={cls(
                        "w-[148px] sm:w-[160px] aspect-square rounded-2xl border shadow-sm",
                        "bg-white border-slate-200",
                        "flex flex-col p-3"
                      )}
                      title="Logged session"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] font-semibold text-slate-500">Session</div>
                        <div className="text-[11px] font-semibold text-slate-400">#{idx + 1}</div>
                      </div>
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-800">
                          {formatClock(ms)}
                        </div>
                      </div>
                      <div className="text-[11px] text-slate-500 text-center">
                        Logged ✓
                      </div>
                    </div>
                  );
                })}

                {/* Next session placeholder / active session card */}
                <div
                  className={cls(
                    "w-[148px] sm:w-[160px] aspect-square rounded-2xl border shadow-sm",
                    hasDraft ? "bg-orange-50 border-orange-200" : "bg-white border-dashed border-slate-300",
                    "flex flex-col p-3"
                  )}
                  title={hasDraft ? (advRunning ? "Session running" : "Session paused") : "Start next session"}
                >
                  <div className="flex items-center justify-between">
                    <div className={cls("text-[11px] font-semibold", hasDraft ? "text-orange-700" : "text-slate-500")}>
                      {hasDraft ? (advRunning ? "In progress" : "Paused") : "Next session"}
                    </div>
                    {hasDraft && (
                      <div className="text-[11px] font-semibold text-orange-600">
                        {advRunning ? "●" : "Ⅱ"}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col items-center justify-center">
                    <div className={cls("text-2xl sm:text-3xl font-extrabold tracking-tight", hasDraft ? "text-slate-800" : "text-slate-400")}>
                      {formatClock(draftMs)}
                    </div>
                    {!hasDraft && (
                      <div className="mt-2 text-[11px] text-slate-500 text-center">
                        Click to start
                      </div>
                    )}
                  </div>

                  {/* Controls (only what makes sense) */}
                  <div className="flex items-center justify-center gap-2">
                    {!hasDraft && (
                      <button
                        type="button"
                        className={cls(
                          "h-9 w-full rounded-xl font-semibold text-white shadow-sm",
                          "bg-orange-500 hover:bg-orange-600 active:scale-[0.98] transition",
                          saving && "opacity-60 cursor-not-allowed"
                        )}
                        disabled={saving}
                        onClick={advancedStart}
                        title="Start session"
                      >
                        <span className="inline-flex items-center justify-center gap-2">
                          <SafeIcon icon={FiPlay} />
                          Start
                        </span>
                      </button>
                    )}

                    {hasDraft && (
                      <>
                        <button
                          type="button"
                          className={cls(
                            "h-9 w-9 rounded-xl border bg-white/80 border-slate-300 hover:bg-slate-100 transition flex items-center justify-center",
                            saving && "opacity-60 cursor-not-allowed"
                          )}
                          disabled={saving}
                          onClick={() => (advRunning ? advancedPause() : advancedStart())}
                          title={advRunning ? "Pause" : "Resume"}
                          aria-label={advRunning ? "Pause" : "Resume"}
                        >
                          <SafeIcon icon={advRunning ? FiPause : FiPlay} />
                        </button>

                        {showReset && (
                          <button
                            type="button"
                            className={cls(
                              "h-9 w-9 rounded-xl border bg-white/80 border-slate-300 hover:bg-slate-100 transition flex items-center justify-center",
                              saving && "opacity-60 cursor-not-allowed"
                            )}
                            disabled={saving}
                            onClick={advancedReset}
                            title="Reset"
                            aria-label="Reset"
                          >
                            <SafeIcon icon={FiRotateCcw} />
                          </button>
                        )}

                        <button
                          type="button"
                          className={cls(
                            // When paused (reset visible), keep this icon-only (square) so it doesn't squash.
                            saveCompact ? "h-9 w-9 rounded-xl" : "h-9 flex-1 rounded-xl",
                            "font-semibold text-white transition flex items-center justify-center",
                            canFinish ? "bg-slate-800 hover:bg-slate-900" : "bg-slate-300 cursor-not-allowed"
                          )}
                          disabled={!canFinish}
                          onClick={advancedFinish}
                          title="Finish & Save"
                          aria-label="Finish & Save"
                        >
                          {saveCompact ? (
                            <SafeIcon icon={FiCheckCircleIcon} />
                          ) : (
                            <span className="inline-flex items-center justify-center gap-2">
                              <SafeIcon icon={FiCheckCircleIcon} />
                              Save
                            </span>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </NodeViewWrapper>
  );
}

/* ───────────────── TipTap Node ───────────────── */

export const PracticeTrackerNode = Node.create({
  name: 'practiceTracker',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      trackerId: { default: null },
      noteBlockId: { default: null },
      lessonNoteId: { default: null },
      studentId: { default: null },
      goal: { default: 5 },
      goalMinutes: { default: 20 },
      trackerMode: { default: 'simple' },

      // ✅ Practiceathon decision (set once on Save)
      applyToPracticeathon: { default: null }, // null until first Save decision; then true/false
      practiceathonId: { default: null },
      practiceathonEnrolmentId: { default: null },
      practiceathonName: { default: null },

      // ✅ One-time early prompt to align tracker mode to practiceathon mode (unsaved only)
      practiceathonModePrompted: { default: false },
    };
  },
  parseHTML() { return [{ tag: 'practice-tracker' }]; },
  renderHTML({ HTMLAttributes }) { return ['practice-tracker', mergeAttributes(HTMLAttributes)]; },
  addNodeView() { return ReactNodeViewRenderer(PracticeTrackerView); },
  addProseMirrorPlugins() {
    const type = this.type;
    const includesTracker = (doc, from, to, nodeType) => {
      let includesAny=false, includesSaved=false;
      doc.nodesBetween(from, to, (n)=>{ if (n.type===nodeType){ includesAny=true; if (n.attrs?.trackerId) includesSaved=true; } return true; });
      return { includesAny, includesSaved };
    };
    return [
      new Plugin({
        props: {
          handleKeyDown(view, event) {
            const sel = view.state.selection;

            // If the tracker node itself is selected, typing ANY printable key will replace the node
            // (default ProseMirror behavior for atom nodes). Prevent that: move cursor after the node
            // and insert the typed character there instead.
            if (sel instanceof NodeSelection && sel.node?.type === type) {
              // Guard backspace/delete (existing behavior)
              if (event.key === 'Backspace' || event.key === 'Delete') {
                if (view?.editable) toast('Use the Delete button to remove the tracker.');
                event.preventDefault();
                return true;
              }

              // Enter: place cursor after node and add paragraph
              if (event.key === 'Enter') {
                event.preventDefault();
                const pos = sel.from; // NodeSelection.from is the node position
                const after = pos + sel.node.nodeSize;
                const tr = view.state.tr
                  .insert(after, view.state.schema.nodes.paragraph.create())
                  .setSelection(TextSelection.create(view.state.doc, after + 1));
                view.dispatch(tr);
                view.focus();
                return true;
              }

              // Printable character with no modifiers: insert after node
              const isPrintable =
                typeof event.key === 'string' &&
                event.key.length === 1 &&
                !event.metaKey && !event.ctrlKey && !event.altKey;
              if (isPrintable) {
                event.preventDefault();
                const pos = sel.from;
                const after = pos + sel.node.nodeSize;
                const tr = view.state.tr
                  .setSelection(TextSelection.create(view.state.doc, after))
                  .insertText(event.key);
                view.dispatch(tr);
                view.focus();
                return true;
              }
              return false;
            }

            if (event.key !== 'Backspace' && event.key !== 'Delete') return false;
            if (sel instanceof NodeSelection && sel.node?.type === type) return false;
            if (sel.from !== sel.to) {
              const { includesAny, includesSaved } = includesTracker(view.state.doc, sel.from, sel.to, type);
              if (includesAny) { if (includesSaved) if (view?.editable) toast('Use the Delete button to remove the tracker.'); event.preventDefault(); return true; }
            }
            return false;
          },
          handleDOMEvents: {
            cut(view, event) {
              const sel = view.state.selection;
              if (sel.from !== sel.to) {
                const { includesAny, includesSaved } = includesTracker(view.state.doc, sel.from, sel.to, type);
                if (includesAny) { if (includesSaved) toast('Use the Delete button to remove the tracker.'); event.preventDefault(); return true; }
              }
              return false;
            },
          },
        },
      }),
    ];
  },
  addKeyboardShortcuts() {
    const handleKey = ({ editor }) => {
      const sel = editor?.state?.selection;
      if (!(sel instanceof NodeSelection) || sel.node?.type?.name !== this.name) return false;
      const { trackerId } = sel.node.attrs || {};
      if (trackerId) {
        if (editor?.isEditable) toast('Use the Delete button to remove this tracker.');
        if (editor?.storage) editor.storage.__ptDeleteIntent = { noteBlockId: sel.node.attrs?.noteBlockId || null };
        return true;
      }
      return false;
    };
    return { Backspace: handleKey, Delete: handleKey };
  },
});

export default PracticeTrackerNode;